import { QueryClient } from '@tanstack/react-query';

/**
 * 공유 QueryClient 싱글턴.
 *
 * `QueryClientProviderWrapper`는 Provider 트리 **안쪽**(layout)에 있고, `AuthProvider`는
 * 그보다 **바깥**에 있어 `useQueryClient()`로는 auth에서 캐시에 닿지 못한다. 그래서 Next.js
 * 표준 패턴대로 브라우저 싱글턴을 두고, Provider와 auth가 **같은 인스턴스**를 공유한다.
 *
 * ⚠️ 서버에서는 요청마다 새 인스턴스를 만들어 사용자 간 캐시 오염을 막는다.
 */
function makeQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 60_000,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: false,
        /**
         * 재시도 없음. RQ 기본 3회(지수 백오프 ~7초)는 실패 갈래를 늦게 보이게 한다.
         * 일시 장애를 버틸 근거가 생기면 여기 한 줄만 1 로 올린다 — 훅마다 박지 않는다.
         */
        retry: 0,
        /**
         * 온라인 여부를 보지 않는다. 기본 `'online'` 은 `onlineManager` 가 offline 로
         * 굳으면(navigator.onLine 이 true 여도 지나간 offline 이벤트를 물고 있을 때) 재시도·첫 시도를
         * `paused` 로 세워 `status` 가 영영 `error` 에 못 간다 — 스피너가 안 풀린다.
         * Firestore SDK 는 오프라인을 스스로 다루므로 이 층에서 멈춰 세울 이유가 없다.
         */
        networkMode: 'always',
      },
    },
  });
}

let browserQueryClient: QueryClient | undefined = undefined;

export function getQueryClient(): QueryClient {
  if (typeof window === 'undefined') {
    // 서버: 요청마다 새 클라이언트 (싱글턴 금지 — 사용자 간 데이터 누수 방지)
    return makeQueryClient();
  }
  if (!browserQueryClient) browserQueryClient = makeQueryClient();
  return browserQueryClient;
}
